import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Row, Col, Button, Modal, ModalBody, Label } from "reactstrap";
import { useFormContext } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import { Icon } from "../../../components/Component";
import FormDatePicker from "../../form-components/DatePicker";
import { Checkbox, Divider, message, Popover, Radio, Space, Tooltip, Modal as AntdModal } from "antd";
import { EditOutlined, InfoCircleOutlined, SettingOutlined } from "@ant-design/icons";
import DeliveryAddressMaster from "../../masters/delivery-address/DeliveryAddressMaster";
import deliveryAddressApis from "../../../api/master/deliveryAddress";
import usersApis from "../../../api/master/users";
import PRApprovalDrawer from "./PRApprovalDrawer";
import { useDispatch, useSelector } from "react-redux";
import contractorsApis from "../../../api/master/contractors";
import misc from "../../../api/misc/dropDowns";
import sites from "../../../api/master/sites";
import sitesApis from "../../../api/master/sites";
import sequenceApis from "../../../api/master/sequence";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import rolesApi from "../../../api/master/roles";

interface SelectOptions {
  label: string;
  value: string;
}
interface SequenceType {
  module: string;
  level: string;
  siteIds: string[];
}

const PURCHASE_REQUEST_MODULE_ID = 2;

const PurchaseRequest_GeneralDetails: FC = (props: any) => {
  const [sequenceAccess, setSequenceAccess] = useState<any>(null);
  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [restartFiscalYear, setRestartFiscalYear] = useState(false);
  const isInitialMount = useRef(true);

  const dispatch: any = useDispatch();

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [blockOptions, setBlockOptions] = useState<SelectOptions[]>([]);
  const [usersOptions, setUsersOptions] = useState<SelectOptions[]>([]);
  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);

  const approvalRequired = watch("approvalRequired");
  const deliveryAddressId = watch("deliveryAddressId");
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>(
    []
  );
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);
  const [towerOptions, setTowerOptions] = useState<SelectOptions[]>([]);
  const [floorOptions, setFloorOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);
  const siteIdWatch = watch("siteId");
  const previousSiteIdRef = useRef<string | undefined>();
  const levelTypeWatch = watch("levelType");
  const workLevelSettingWatch = watch("workLevelSetting");
  const towerBlockVillaIdWatch = watch("towerBlockVillaId");
  const floorNoWatch = watch("floorNo");
  const flatIdWatch = watch("flatId");
  const locationIdWatch = watch("locationId");
  const AllFormDetails = watch();

  const SEGMENT_TO_WORK_LEVEL: Record<string, string> = {
    Tower: "TOWER_BLOCK",
    "Tower/Block": "TOWER_BLOCK",
    "Tower / Block": "TOWER_BLOCK",
    "Tower/ Block": "TOWER_BLOCK",
    Block: "TOWER_BLOCK",
    Villa: "VILLA",
    Floor: "FLOOR",
    Flat: "FLAT",
  };

  const WORK_LEVEL_TO_SEGMENT: Record<string, string> = {
    TOWER_BLOCK: "Tower",
    VILLA: "Villa",
    FLOOR: "Floor",
    FLAT: "Flat",
  };

  const HIERARCHY_UNIT_TITLES: Record<string, string> = {
    TOWER_BLOCK: "Tower / Block",
    VILLA: "Villa",
    FLOOR: "Tower / Block",
    FLAT: "Tower / Block",
  };

  const hierarchyFieldLabel = useMemo(() => {
    if (!workLevelSettingWatch || workLevelSettingWatch === "NONE") {
      return "Tower / Block";
    }
    return HIERARCHY_UNIT_TITLES[workLevelSettingWatch] ?? "Tower / Block";
  }, [workLevelSettingWatch]);

  const hierarchyFieldPlaceholder = useMemo(() => {
    switch (workLevelSettingWatch) {
      case "VILLA":
        return "Select Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Select Tower / Block";
      default:
        return "Select Tower / Block / Villa";
    }
  }, [workLevelSettingWatch]);

  const shouldShowHierarchy =
    workLevelSettingWatch && workLevelSettingWatch !== "NONE";

  const [isWorkLevelDisabled, setIsWorkLevelDisabled] = useState(true);

  const fetchSiteHierarchy = async (siteId: any) => {
    try {
      const { data, status } = await sitesApis.fetchSegmentLevelBySiteId(
        siteId
      );
      if (status && data) {
        const workLevelValue = SEGMENT_TO_WORK_LEVEL[data] || null;
        setValue("workLevelSetting", workLevelValue);
        setIsWorkLevelDisabled(true);
      } else {
        // If no data is returned, show "Site" by clearing the field
        setValue("workLevelSetting", null);
        setIsWorkLevelDisabled(true);
      }
    } catch (error) {
      console.log("Error fetching site hierarchy:", error);
      setValue("workLevelSetting", null);
      setIsWorkLevelDisabled(true);
    }
  };

  useEffect(() => {
    fetchSites();
    const { approvalsData } = AllFormDetails;
    if (approvalsData) {
      setApprovals(JSON.parse(approvalsData));
    }
    // Restore numberingMode from saved form values on remount
    const savedNumberingMode = getValues("numberingMode");
    if (savedNumberingMode) {
      setNumberingMode(savedNumberingMode);
    }
  }, []);

  useEffect(() => {
    const previousSiteId = previousSiteIdRef.current;
    const siteChanged =
      previousSiteId !== undefined && previousSiteId !== siteIdWatch;
    if (siteIdWatch) {
      fetchUsers(siteIdWatch);
      fetchBlockList(siteIdWatch);
      fetchDeliveryAddressBySite(siteIdWatch);
      fetchSiteHierarchy(siteIdWatch);
      if (siteChanged) {
        setValue("deliveryAddressId", "");
        setValue("towerBlockVillaId", null);
        setValue("floorNo", null);
        setValue("flatId", null);
        setValue("locationId", null);
      }
    } else {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      setLocationOptions([]);
      setValue("workLevelSetting", null);
      setValue("segmentLevel", null);
      setIsWorkLevelDisabled(false);
    }
    previousSiteIdRef.current = siteIdWatch;
  }, [setValue, siteIdWatch]);

  // useEffect(() => {
  //   fetchSequenceAccess();
  // }, []);

  useEffect(() => {
    if (siteIdWatch) {
      fetchSequenceAccess();
    } else {
      setActiveSequences([]);
    }
  }, [siteIdWatch]);

  const loadTowerOptions = useCallback(
    async (siteId?: string | number) => {
      const resolvedSiteId = siteId ?? siteIdWatch;
      if (!resolvedSiteId) {
        setTowerOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
          siteId: resolvedSiteId,
        });
        if (response?.status && Array.isArray(response.data)) {
          setTowerOptions(
            response.data.map((unit: any) => ({
              value: unit.id,
              label: unit.unitName,
            }))
          );
        } else {
          setTowerOptions([]);
        }
      } catch (error) {
        console.log("Failed to load tower/block options", error);
        setTowerOptions([]);
      }
    },
    [siteIdWatch]
  );

  const loadFloorOptions = useCallback(
    async (unitId?: number | string) => {
      if (!siteIdWatch || !unitId) {
        setFloorOptions([]);
        return;
      }
      try {
        const response =
          await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
            siteId: siteIdWatch,
            unitId,
          });
        if (response?.status && Array.isArray(response.data)) {
          setFloorOptions(
            response.data.map((floor: any) => ({
              value: floor.floorNo,
              label: floor.floorNo,
            }))
          );
        } else {
          setFloorOptions([]);
        }
      } catch (error) {
        console.log("Failed to load floor options", error);
        setFloorOptions([]);
      }
    },
    [siteIdWatch]
  );

  const loadFlatOptions = useCallback(
    async (unitId?: number | string, floorNo?: string | null) => {
      if (!siteIdWatch || !unitId) {
        setFlatOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
          siteId: siteIdWatch,
          unitId,
          floorNo: floorNo ?? undefined,
        });
        if (response?.status && Array.isArray(response.data)) {
          setFlatOptions(
            response.data.map((flat: any) => ({
              value: flat.id,
              label: flat.subUnitName || flat.unitName,
            }))
          );
        } else {
          setFlatOptions([]);
        }
      } catch (error) {
        console.log("Failed to load flat options", error);
        setFlatOptions([]);
      }
    },
    [siteIdWatch]
  );

  const loadLocationOptions = useCallback(async () => {
    if (!siteIdWatch) {
      setLocationOptions([]);
      return;
    }
    try {
      const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
        siteId: siteIdWatch,
      });
      if (response?.status && Array.isArray(response.data)) {
        setLocationOptions(
          response.data.map((loc: any) => ({
            value: loc.id,
            label: loc.locationName,
          }))
        );
      } else {
        setLocationOptions([]);
      }
    } catch (error) {
      console.log("Failed to load location options", error);
      setLocationOptions([]);
    }
  }, [siteIdWatch]);

  useEffect(() => {
    if (
      workLevelSettingWatch &&
      workLevelSettingWatch !== "NONE" &&
      siteIdWatch
    ) {
      loadTowerOptions(siteIdWatch);
      loadLocationOptions();
    } else {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      setLocationOptions([]);
      setValue("towerBlockVillaId", null);
      setValue("floorNo", null);
      setValue("flatId", null);
      setValue("locationId", null);
    }

    if (!workLevelSettingWatch || workLevelSettingWatch === "NONE") {
      setValue("segmentLevel", null);
    } else {
      setValue(
        "segmentLevel",
        WORK_LEVEL_TO_SEGMENT[workLevelSettingWatch] ?? null
      );
    }
  }, [
    loadLocationOptions,
    loadTowerOptions,
    setValue,
    siteIdWatch,
    workLevelSettingWatch,
  ]);

  useEffect(() => {
    if (!shouldShowHierarchy) {
      setFloorOptions([]);
      setFlatOptions([]);
      return;
    }
    if (
      (workLevelSettingWatch === "TOWER_BLOCK" ||
        workLevelSettingWatch === "VILLA" ||
        workLevelSettingWatch === "FLOOR" ||
        workLevelSettingWatch === "FLAT") &&
      towerBlockVillaIdWatch
    ) {
      loadFloorOptions(towerBlockVillaIdWatch);
    } else {
      setFloorOptions([]);
      setValue("floorNo", null);
    }
  }, [
    loadFloorOptions,
    setValue,
    shouldShowHierarchy,
    towerBlockVillaIdWatch,
    workLevelSettingWatch,
  ]);

  // Clear hierarchy fields in existing line items when workLevelSetting changes
  const previousWorkLevelSettingRef = useRef<string | undefined>();
  useEffect(() => {
    const currentWorkLevelSetting = workLevelSettingWatch;
    const previousWorkLevelSetting = previousWorkLevelSettingRef.current;

    // Only clear if workLevelSetting actually changed (not on initial load)
    if (
      previousWorkLevelSetting !== undefined &&
      previousWorkLevelSetting !== currentWorkLevelSetting &&
      previousWorkLevelSetting !== null &&
      currentWorkLevelSetting !== null
    ) {
      const existingItems = getValues("items") || [];
      if (existingItems.length > 0) {
        // Clear hierarchy fields in all existing items
        existingItems.forEach((item: any, index: number) => {
          setValue(`items.${index}.towerBlockVillaId`, null);
          setValue(`items.${index}.floorNo`, null);
          setValue(`items.${index}.flatId`, null);
          setValue(`items.${index}.locationId`, null);
          setValue(`items.${index}.workLevelSetting`, currentWorkLevelSetting);
        });
      }
    }

    // Update the ref for next comparison
    previousWorkLevelSettingRef.current = currentWorkLevelSetting;
  }, [workLevelSettingWatch, getValues, setValue]);

  useEffect(() => {
    if (
      shouldShowHierarchy &&
      workLevelSettingWatch === "FLAT" &&
      towerBlockVillaIdWatch
    ) {
      loadFlatOptions(towerBlockVillaIdWatch, floorNoWatch ?? null);
    } else {
      setFlatOptions([]);
      if (workLevelSettingWatch !== "FLAT") {
        setValue("flatId", null);
      }
    }
  }, [
    floorNoWatch,
    loadFlatOptions,
    setValue,
    shouldShowHierarchy,
    towerBlockVillaIdWatch,
    workLevelSettingWatch,
  ]);

  useEffect(() => {
    if (!shouldShowHierarchy) {
      return;
    }
    if (
      workLevelSettingWatch === "FLAT" &&
      towerBlockVillaIdWatch &&
      floorNoWatch &&
      !flatIdWatch
    ) {
      // reset flat options when floor changes
      loadFlatOptions(towerBlockVillaIdWatch, floorNoWatch);
    }
  }, [
    flatIdWatch,
    floorNoWatch,
    loadFlatOptions,
    shouldShowHierarchy,
    towerBlockVillaIdWatch,
    workLevelSettingWatch,
  ]);

  useEffect(() => {
    if (!shouldShowHierarchy) {
      setValue("towerBlockVillaLabel", "");
      return;
    }
    const selectedTower = towerOptions.find(
      (option) => option.value == towerBlockVillaIdWatch
    );
    setValue("towerBlockVillaLabel", selectedTower?.label ?? "");
  }, [shouldShowHierarchy, towerBlockVillaIdWatch, towerOptions, setValue]);

  useEffect(() => {
    if (!shouldShowHierarchy) {
      setValue("floorLabel", "");
      return;
    }
    setValue(
      "floorLabel",
      floorNoWatch != null && floorNoWatch !== "" ? floorNoWatch : ""
    );
  }, [shouldShowHierarchy, floorNoWatch, setValue]);

  useEffect(() => {
    if (!shouldShowHierarchy) {
      setValue("flatLabel", "");
      return;
    }
    const selectedFlat = flatOptions.find(
      (option) => option.value == flatIdWatch
    );
    setValue("flatLabel", selectedFlat?.label ?? "");
  }, [shouldShowHierarchy, flatIdWatch, flatOptions, setValue]);

  useEffect(() => {
    if (!shouldShowHierarchy) {
      setValue("locationLabel", "");
      return;
    }
    const selectedLocation = locationOptions.find(
      (option) => option.value == locationIdWatch
    );
    setValue("locationLabel", selectedLocation?.label ?? "");
  }, [shouldShowHierarchy, locationIdWatch, locationOptions, setValue]);

  const submitForm = async (data: any) => {
    setValue("approvalsData", JSON.stringify(approvals));
    setValue("isStrictApproval", isStrictApproval);
    setValue("numberingMode", numberingMode);
    if (approvalRequired == "true" && approvals?.data?.length === 0) {
      return;
    }

    // Check if the logged-in user is the final approver for the Purchase Request
    // module. Run regardless of approvalRequired so downstream qty validation
    // can rely on the flag to bypass the block for admin/final approvers.
    if (siteIdWatch) {
      try {
        const res = await rolesApi.fetchHierarchyUsersByModuleId(
          PURCHASE_REQUEST_MODULE_ID,
          siteIdWatch,
        );
        const msg = (res?.message || "").toString().toLowerCase();
        setValue("isFinalApprover", msg.includes("you are the final"));
      } catch (err) {
        console.log("Error checking final approver:", err);
        setValue("isFinalApprover", false);
      }
    } else {
      setValue("isFinalApprover", false);
    }

    props?.next();
  };

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        let sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUsers = async (siteId: any) => {
    try {
      const data = await misc.fetchSiteUsers(siteId);
      if (data) {
        let usersOptions = data?.data?.map((item: any) => ({
          label: item.userName,
          value: item.userId.toString(),
        }));
        setUsersOptions(usersOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const fetchBlockList = async (siteId: any) => {
    try {
      const { data, status } = await sites.fetchBlockById(siteId);
      if (status) {
        let blockOptions = data.map((item: any) => ({
          label: item.blockName,
          value: item.blockID.toString(),
        }));
        setBlockOptions(blockOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const ShowAddressPopOver = ({ id, text, options, matchKey }: any) => {
    if (!id) {
      return <></>;
    }

    let address = options?.find((item: any) => item[matchKey] == id);
    if (!address) {
      return <></>;
    }
    return (
      <Popover
        content={
          <ul>
            <li>
              {address?.addressLine1} {address?.addressLine2}
            </li>
            <li>
              {address?.city},{address?.districtName}, {address?.stateName},
              {address?.countryName} - {address?.pincode}
            </li>
          </ul>
        }
        title={address?.deliveryLocationName}
      >
        <a>{text}</a>
      </Popover>
    );
  };

  const fetchDeliveryAddressBySite = async (siteId: any) => {
    try {
      const { data, status } = await deliveryAddressApis.fetchAddressBySite(
        siteId
      );

      if (status) {
        let deliveryAddressOptions = data.map((item: any) => ({
          label: item.deliveryLocationName,
          value: item.id.toString(),
        }));
        setDeliveryAddressOptions(deliveryAddressOptions);
        setDeliveryAddressList(data);
      }
    } catch (error) {
      console.log("Error fetching delivery addresses by site:", error);
    }
  };

  const handleModelPopUp = (id?: any, type?: string) => {
    const content = (
      <DeliveryAddressMaster
        formAction={type}
        id={id}
        toggleModelPopUp={() => {
          setIsOpen(false);
          if (siteIdWatch) {
            fetchDeliveryAddressBySite(siteIdWatch);
          }
        }}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };
  useEffect(() => {
    if (siteIdWatch) {
      fetchContractorList(siteIdWatch);
    }
  }, [siteIdWatch]);

  const fetchContractorList = async (siteId: any) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: siteId,
      });
      if (status) {
        let contractorOptions = data.map((item: any) => ({
          label: item?.companyName,
          value: item?.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleId === PURCHASE_REQUEST_MODULE_ID &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      setActiveSequences(_sequenceAccess || []);

      // Check if mode is already persisted in form (e.g. user navigated back from next step)
      const savedMode = getValues("numberingMode");
      if (savedMode) {
        setNumberingMode(savedMode);
        return;
      }

      // Set initial numbering mode based on available sequences
      if (_sequenceAccess && _sequenceAccess.length > 0 && siteIdWatch) {
        // Check if current site has a sequence
        const siteHasSequence = _sequenceAccess.some(
          (seq: any) => seq.siteId?.toString() === siteIdWatch?.toString() || seq.level === 'Org'
        );
        setNumberingMode(siteHasSequence ? 'auto' : 'manual');
      } else {
        setNumberingMode('manual');
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setActiveSequences([]);
      setNumberingMode('manual');
    }
  };

  const getCurrentSiteSequence = () => {
    if (!siteIdWatch || activeSequences.length === 0) return null;

    const siteSequence = activeSequences.find(
      (seq) =>
        seq.level === "Site" &&
        seq.siteId?.toString() === siteIdWatch?.toString()
    );

    if (siteSequence) return siteSequence;

    const orgSequence = activeSequences.find(
      (seq) => seq.level === "Org"
    );

    if (orgSequence) return orgSequence;

    return null;
  };

  const currentSiteSequence = getCurrentSiteSequence();
  const canAuto = !!currentSiteSequence;

  // Auto-generate number when in auto mode
  useEffect(() => {
    const existingValue = getValues("purchaseRequestId");

    if (numberingMode === 'auto' && currentSiteSequence && !existingValue) {
      const { prefix } = currentSiteSequence;
      const prNumber = `${prefix}`;
      setValue('purchaseRequestId', prNumber);
    }
  }, [numberingMode, currentSiteSequence]);

  // Reset number when site changes (skip initial mount to preserve values on remount)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    setValue("purchaseRequestId", "");
    setNumberingMode("manual");
  }, [siteIdWatch, setValue]);

  const prevSiteRef = useRef();

  // Set initial mode based on site selection (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const prevSite = prevSiteRef.current;

    // Only run if site actually changed (not on remount with same site)
    if (prevSite && prevSite !== siteIdWatch) {
      setValue("purchaseRequestId", "");
      // Clear persisted mode so new site gets fresh evaluation
      setValue("numberingMode", "");
    }

    // Respect persisted mode on remount
    const savedMode = getValues("numberingMode");
    if (savedMode) {
      setNumberingMode(savedMode);
    } else if (!siteIdWatch) {
      setNumberingMode("manual");
    } else {
      const siteSequence = getCurrentSiteSequence();
      setNumberingMode(siteSequence ? "auto" : "manual");
    }

    prevSiteRef.current = siteIdWatch;
  }, [siteIdWatch]);

  // Set levelType to "Site" by default
  useEffect(() => {
    if (!levelTypeWatch) {
      setValue("levelType", "Site");
    }
  }, [levelTypeWatch, setValue]);

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <Row className="gy-4">
          {/* <Col lg="4">
            <FormSelect
              name="levelType"
              label={`Level Type`}
              options={[
                { label: "Site", value: "Site" },
                { label: "Block", value: "Block" },
              ]}
              required={true}
              placeholder="-Select-"
              tooltipText="Select the site where the materials will be used."
            />
          </Col> */}
          <Col lg="4">
            <FormSelect
              name="siteId"
              label={`Site`}
              options={sitesOptions}
              required={true}
              placeholder="-Select-"
              tooltipText="Select the site where the materials will be used."
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="workLevelSetting"
              label="Site Hierarchy"
              options={[
                { label: "Tower / Block", value: "TOWER_BLOCK" },
                { label: "Villa", value: "VILLA" },
                { label: "Floor", value: "FLOOR" },
                { label: "Flat", value: "FLAT" },
              ]}
              placeholder={
                siteIdWatch && isWorkLevelDisabled && !workLevelSettingWatch
                  ? "Site"
                  : "-Select-"
              }
              tooltipText="Select the hierarchy level for material requirement."
              disabled={!siteIdWatch || isWorkLevelDisabled}
            />
          </Col>

          {/* {(sequenceAccess?.level == "Site" &&
            !sequenceAccess?.siteIds?.includes(siteIdWatch)) ||
            sequenceAccess?.module == "" ? (
            <Col lg="4">
              <FormInput
                name="purchaseRequestId"
                label={`Purchase Request Number`}
                required={true}
                placeholder="Enter Purchase Request Number"
              />
            </Col>
          ) : (
            <></>
          )} */}

          <Col lg="4">
            <div style={{ position: 'relative' }}>
              <FormInput
                name="purchaseRequestId"
                label="Purchase Request Number"
                required={numberingMode === 'manual'}
                disabled={numberingMode === 'auto' || AllFormDetails?.type === "draft"}
                placeholder={numberingMode === 'auto' ? 'Auto-generated' : 'Enter PR Number'}
              />
              <Tooltip title="Click here to enable or disable auto-generation of PR numbers.">
                <SettingOutlined
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50px',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#1890ff',
                    zIndex: 1,
                  }}
                  onClick={() => {
                    setTempNumberingMode(numberingMode);
                    setShowNumberingModal(true);
                  }}
                />
              </Tooltip>
            </div>
          </Col>

          <Col lg="4">
            <FormInput
              name="referenceIndentNumber"
              label="Indent Number"
              required={false}
              type="text"
              placeholder="Enter Indent Number"
              tooltipText="Enter Indent Number."
            />
          </Col>
          <Col lg="4">
            <FormDatePicker
              name="indentDate"
              label="Indent Date"
              required={true}
              minDate={new Date()}
            />
          </Col>

          <Col lg="4">
            <FormSelect
              name="contractorId"
              label={`Contractor`}
              required={true}
              options={contractorOptions}
              placeholder="-Select-"
              tooltipText="Select the contractor for this purchase request."
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="requestedBy"
              label={`Requested By`}
              required={true}
              options={usersOptions}
              placeholder="-Select-"
            />
          </Col>
          <Col lg="4">
            <FormDatePicker
              name="needByDate"
              label="Need By Date"
              required={true}
              minDate={new Date()}
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="approvalRequired"
              label="Approval Required ?"
              options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
              ]}
              placeholder="Select"
              disabled={!siteIdWatch}
              required={true}
            />
            <div style={{ display: "none" }}>
              <FormInput name="approvalsData" label="" type="text" noMaxLength />
            </div>
            {approvalRequired == "true" && (
              <Popover
                content="Click here to set required approvals"
                title="Approval Required"
              >
                <a
                  href="#"
                  className="success"
                  onClick={(e) => {
                    e.preventDefault();
                    setApprovals((pre) => {
                      return {
                        ...pre,
                        open: true,
                      };
                    });
                  }}
                >
                  <EditOutlined /> Set Approvals &nbsp;
                  {approvals?.data?.length == 0 ? (
                    <span className="mandatory">Please set approvals</span>
                  ) : (
                    <></>
                  )}
                </a>
              </Popover>
            )}
          </Col>
          <Col lg="4">
            <FormSelect
              name="deliveryAddressId"
              label="Delivery Address"
              options={deliveryAddressOptions}
              required={true}
              placeholder={siteIdWatch ? "-Select-" : "Select a site first"}
              disabled={!siteIdWatch}
              tooltipText="Select the delivery address for the Purchase Request."
              popOverOptions={
                <ShowAddressPopOver
                  id={deliveryAddressId}
                  matchKey="id"
                  options={deliveryAddressList}
                  text="View Selected Delivery Address"
                />
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <Space
                    style={{
                      padding: "0 8px 4px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      type="button"
                      color="primary"
                      size="small"
                      onClick={() => {
                        handleModelPopUp(null, "");
                      }}
                      style={{
                        width: "100%",
                        textAlign: "center",
                      }}
                      disabled={!siteIdWatch}
                    >
                      <span>Add Address</span>
                    </Button>
                  </Space>
                </>
              )}
            />
          </Col>
        </Row>
        <Row className="gy-4 mt-1">
          <Col>
            <Button color="primary" type="submit">
              Next
            </Button>
          </Col>
        </Row>
      </form>
      {approvals.open && (
        <PRApprovalDrawer
          open={approvals.open}
          siteId={siteIdWatch}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status: boolean) => { }}
          approvalData={approvals.data} // Pass the current approval data
        />
      )}
      <Modal
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
        className="modal-dialog-centered"
        size="xl"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setIsOpen(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>

        <ModalBody>{popUpContent}</ModalBody>
      </Modal>

      {/* Purchase Request Number Configuration Modal */}
      <AntdModal
        title="Configure Purchase Request Number Preference"
        open={showNumberingModal}
        onCancel={() => setShowNumberingModal(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px" }}>
            <Button
              key="save"
              color="primary"
              onClick={() => {
                if (tempNumberingMode === "auto" && !currentSiteSequence) {
                  message.error("Auto-generation is not configured for this site.");
                  return;
                }

                setNumberingMode(tempNumberingMode);

                if (tempNumberingMode === "auto" && currentSiteSequence) {
                  const { prefix } = currentSiteSequence;
                  const prNumber = `${prefix}`;
                  setValue("purchaseRequestId", prNumber);
                }

                else if (tempNumberingMode === "manual") {
                  setValue("purchaseRequestId", "");
                }
                setShowNumberingModal(false);
              }}
            >
              Save
            </Button>

            <Button
              key="cancel"
              color="primary"
              onClick={() => setShowNumberingModal(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 20, lineHeight: "22px" }}>
          Your purchase request numbers are currently set to auto-generate to save time.
          <br />
          Are you sure you want to change this setting?
        </div>

        {!siteIdWatch && (
          <div style={{ color: "#8c8c8c", marginBottom: 16 }}>
            <InfoCircleOutlined /> Sequence configuration is not set for this site. Please configure the sequence.
          </div>
        )}

        {siteIdWatch && !currentSiteSequence && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined />{" "}
            {activeSequences.length === 0
              ? "No sequences configured for Purchase Requests. Please set up a sequence first."
              : "No sequence configured for the selected site. Please set a sequence for this site."}
          </div>
        )}

        <Radio.Group
          onChange={(e) => setTempNumberingMode(e.target.value)}
          value={tempNumberingMode}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Radio value="auto" disabled={!canAuto}>
              <span style={{ fontWeight: 500 }}>
                Continue auto-generating purchase request numbers
              </span>
            </Radio>

            <Radio value="manual">
              <span style={{ fontWeight: 500 }}>
                Enter purchase request numbers manually
              </span>
            </Radio>
          </Space>
        </Radio.Group>
      </AntdModal>
    </>
  );
};

export default PurchaseRequest_GeneralDetails;
